import { createFileRoute, notFound } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { getPageAdmin, updatePage, generatePageSlug } from "@/lib/api/pages.functions";
import { AdminFormFooter } from "@/components/admin/AdminFormFooter";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { CraftEditor, type CraftEditorHandle } from "@/components/admin/CraftEditor";
import { ContentAdminTabs } from "@/components/admin/ContentAdminTabs";
import { PageSettingsForm } from "@/components/admin/PageSettingsForm";
import { toast } from "sonner";
import { ogFromPage, type MediaSelection } from "@/lib/media/types";
import { isReservedPageSlug } from "@/lib/craft/reserved-slugs";
import { stringifyCraftState } from "@/lib/craft/serialize";
import { resolveCraftForSave } from "@/lib/craft/use-craft-save";

export const Route = createFileRoute("/admin/pages/$id")({
  loader: ({ params }) => getPageAdmin({ data: { id: params.id } }),
  component: EditPageAdmin,
});

function EditPageAdmin() {
  const page = Route.useLoaderData();
  if (!page) throw notFound();

  const [title, setTitle] = useState(page.title);
  const [slug, setSlug] = useState(page.slug);
  const [status, setStatus] = useState(page.status);
  const [metaTitle, setMetaTitle] = useState(page.metaTitle ?? "");
  const [metaDescription, setMetaDescription] = useState(page.metaDescription ?? "");
  const [craftState, setCraftState] = useState(stringifyCraftState(page.craftState));
  const [craftCustomCss, setCraftCustomCss] = useState(page.craftCustomCss ?? "");
  const [ogImage, setOgImage] = useState<MediaSelection>(ogFromPage(page));
  const [saving, setSaving] = useState(false);
  const craftEditorRef = useRef<CraftEditorHandle>(null);

  async function handleSave() {
    if (slug && isReservedPageSlug(slug) && slug !== "home" && slug !== "about" && slug !== page.slug) {
      toast.error(`Slug "${slug}" is reserved for an existing route.`);
      return;
    }
    const craft = resolveCraftForSave(craftEditorRef, { craftState, craftCustomCss });
    setSaving(true);
    try {
      await updatePage({
        data: {
          id: page.id,
          title,
          slug,
          status,
          craftState: craft.serialized,
          craftCustomCss: craft.customCss,
          metaTitle,
          metaDescription,
          ogImageMediaId: ogImage.mediaId,
          isSystemPage: page.isSystemPage,
        },
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Edit page"
        badge={
          page.isSystemPage ? (
            <span className="rounded bg-muted px-2 py-1 text-xs font-medium">System page</span>
          ) : undefined
        }
      />
      <ContentAdminTabs
        settingsLabel="Page settings"
        content={
          <CraftEditor
            ref={craftEditorRef}
            key={page.id}
            className="w-full"
            initialState={page.craftState}
            customCss={craftCustomCss}
            onChange={(serialized, css) => {
              setCraftState(serialized);
              setCraftCustomCss(css);
            }}
            onCustomCssChange={setCraftCustomCss}
          />
        }
        settingsForm={
          <PageSettingsForm
            title={title}
            onTitleChange={setTitle}
            slug={slug}
            onSlugChange={setSlug}
            onGenerateSlug={() => generatePageSlug({ data: { title, excludeId: page.id } })}
            status={status}
            onStatusChange={setStatus}
            showArchivedStatus
            metaTitle={metaTitle}
            onMetaTitleChange={setMetaTitle}
            metaDescription={metaDescription}
            onMetaDescriptionChange={setMetaDescription}
            ogImage={ogImage}
            onOgImageChange={setOgImage}
          />
        }
      />
      <AdminFormFooter cancelTo="/admin/pages" onSave={handleSave} saving={saving} />
    </AdminPageShell>
  );
}
